import { Capacitor } from '@capacitor/core';

// Base URL prepended to every API request. Empty by default, which preserves
// today's behavior (relative paths, working via CRA's dev proxy or same-origin
// production hosting). Set REACT_APP_API_URL at build time if the frontend and
// backend are deployed to different origins.
const BUILT_IN_API_URL = process.env.REACT_APP_API_URL || '';

// ── Runtime API host override (packaged app only) ──────────────────────────
//
// REACT_APP_API_URL is frozen at build time, which is wrong for the Android
// test build: it points at this laptop's Wi-Fi address, and that address is a
// DHCP lease that changes every time the laptop joins a different network.
// Each change meant editing two files that have to agree (.env.production.local
// and android/.../network_security_config.xml), rebuilding, and re-installing
// -- and when it was missed, the app just showed an empty storefront with no
// error, because the requests were going to an address that no longer existed.
//
// The override lets the value be retyped on the device instead. It is read
// once here at module load, so API_BASE_URL stays a plain constant and every
// existing call site keeps working untouched; changing it reloads the app.
export const API_OVERRIDE_KEY = 'acymart_api_base_override';

// Scheme + host + optional port, nothing else. A path would be silently
// concatenated with every endpoint ("/api/products" appended to a URL that
// already ends in "/v1" gives a 404 that looks like a server fault), and a
// non-http scheme cannot be fetched at all.
const HOST_ONLY = /^https?:\/\/[^/\s?#]+$/;

export function normalizeApiBase(value) {
  const raw = String(value ?? '').trim().replace(/\/+$/, '');
  if (!raw) return '';
  return HOST_ONLY.test(raw) ? raw : null; // null = rejected, '' = cleared
}

// Deliberately restricted to the Capacitor shell. On the web this would be a
// way to repoint a logged-in session's API traffic -- including its auth
// cookie -- at an attacker-chosen host via nothing more than a localStorage
// write, so the browser ignores any override that happens to be set.
export const canOverrideApiBase = () => Capacitor.isNativePlatform();

function readOverride() {
  if (!canOverrideApiBase()) return '';
  try {
    return normalizeApiBase(localStorage.getItem(API_OVERRIDE_KEY)) || '';
  } catch {
    return ''; // storage disabled -- fall back to the built-in value
  }
}

export const API_BASE_URL = readOverride() || BUILT_IN_API_URL;

// Exposed so the settings screen can show what shipped in the build versus
// what is actually in force right now.
export const apiBaseInfo = () => ({
  active: API_BASE_URL,
  builtIn: BUILT_IN_API_URL,
  override: readOverride(),
  supported: canOverrideApiBase(),
});

// Persists and returns true when the caller should reload. Reload rather than
// live-patching: API_BASE_URL is captured by module scope in dozens of files,
// so a fresh boot is the only way to make every call site agree.
export function setApiOverride(value) {
  const next = normalizeApiBase(value);
  if (next === null) return false;
  try {
    if (next) localStorage.setItem(API_OVERRIDE_KEY, next);
    else localStorage.removeItem(API_OVERRIDE_KEY);
    return true;
  } catch {
    return false;
  }
}

// Resolves an uploaded-media path against the API host.
//
// Product images are stored as server-relative paths ("/uploads/xyz.png").
// On the web that resolves correctly because the page and the API share an
// origin (or the dev proxy forwards it). In the Capacitor build the page is
// served from capacitor://localhost (iOS) or https://localhost (Android),
// where nothing is listening -- so every one of those paths 404s and the app
// shows a storefront with no product photos.
//
// Anything already absolute is returned untouched: remote placeholder URLs,
// data: URIs, and the blob: URLs the product forms create for a local preview
// before upload (prefixing one of those would break the preview).
export function mediaUrl(src) {
  if (!src || typeof src !== 'string') return src;
  if (/^(https?:|data:|blob:|capacitor:|file:)/i.test(src)) return src;
  if (!src.startsWith('/')) return src;
  return `${API_BASE_URL}${src}`;
}

// Widths written for every uploaded image. Must match RESPONSIVE_WIDTHS in
// backend/middleware/imageProcessing.js and WIDTHS in
// backend/scripts/backfillResponsive.js -- all three name the same files on
// disk, and there is nothing at runtime that can detect a disagreement. A
// width listed here but not written there makes the browser fetch a 404.
export const MEDIA_WIDTHS = [400, 800];

// Builds the srcSet for an uploaded product image.
//
// The backend stores ONE url per image and the variants are found purely by
// naming convention -- `/uploads/abc.png` implies `/uploads/abc-400.webp` --
// so this reverses exactly the transformation backfillResponsive.js applies.
// Dropping the original extension is safe because upload filenames carry a
// timestamp and a random slug, so two uploads cannot differ by extension alone.
//
// Returns undefined rather than a string for anything not served from our own
// /uploads: remote placeholder URLs, data: and blob: previews in the product
// editor, and seller-supplied absolute image links all have no variants, and a
// srcSet pointing at names that were never written is worse than no srcSet.
// An <img> with srcSet={undefined} simply uses src, which is the correct
// fallback for an image the backfill has not reached yet.
export function mediaSrcSet(src) {
  if (!src || typeof src !== 'string') return undefined;

  // Callers pass whatever they already had. Some hand over the raw stored
  // path ("/uploads/abc.png"), others the output of mediaUrl(), which in the
  // packaged app is absolute ("https://acymart.com/uploads/abc.png"). Both
  // have to work, so normalise to the stored path first and let mediaUrl
  // re-resolve at the end -- otherwise the app would build variant URLs with
  // the host prefixed twice.
  const relative = API_BASE_URL && src.startsWith(`${API_BASE_URL}/uploads/`)
    ? src.slice(API_BASE_URL.length)
    : src;
  if (!relative.startsWith('/uploads/')) return undefined;

  const stem = relative.replace(/\.[^./]+$/, '');
  if (stem === relative) return undefined; // no extension to swap -- not one of ours

  return MEDIA_WIDTHS
    .map((w) => `${mediaUrl(`${stem}-${w}.webp`)} ${w}w`)
    .concat(`${mediaUrl(relative)} 1600w`)
    .join(', ');
}
